/**
 * @description
 * System configuration component for marketplace settings.
 * Handles marketplace-specific configuration (not Cloudflare credentials).
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { SystemConfigurationProps } from './types';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';

/**
 * System configuration component
 */
export function SystemConfiguration({ onRefresh }: SystemConfigurationProps) {
  const { config, updateConfig } = useMarketplaceConfig();
  const [formData, setFormData] = useState({
    defaultWorkerSettings: config?.defaultWorkerSettings || {
      region: 'auto',
      compatibilityDate: '2024-01-01',
      compatibilityFlags: ['nodejs_compat']
    },
    jwtSecretTemplate: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await updateConfig(formData);
      await onRefresh();
      toast.success('Marketplace configuration updated successfully');
    } catch (error) {
      console.error('Error updating marketplace configuration:', error);
      toast.error('Failed to update marketplace configuration');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Marketplace Configuration</h2>
        <p className="text-sm text-muted-foreground">
          Configure marketplace-specific settings. Cloudflare credentials are managed in the global settings.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Cloudflare Settings
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>Cloudflare credentials are managed in the global settings. Visit <a href="/settings" className="underline">Settings → Cloudflare API</a> to configure your Cloudflare Account ID and API Token.</p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="compatibilityDate" className="block text-sm font-medium mb-2">
            Worker Compatibility Date
          </label>
          <input
            id="compatibilityDate"
            type="text"
            value={formData.defaultWorkerSettings.compatibilityDate}
            onChange={(e) => handleInputChange('defaultWorkerSettings', {
              ...formData.defaultWorkerSettings,
              compatibilityDate: e.target.value
            })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder="2024-01-01"
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            Cloudflare Workers compatibility date for marketplace apps.
          </p>
        </div>

        <div>
          <label htmlFor="compatibilityFlags" className="block text-sm font-medium mb-2">
            Compatibility Flags
          </label>
          <div className="space-y-2">
            {['nodejs_compat', 'streams', 'webcrypto'].map((flag) => (
              <label key={flag} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={formData.defaultWorkerSettings.compatibilityFlags.includes(flag)}
                  onChange={(e) => {
                    const flags = e.target.checked
                      ? [...formData.defaultWorkerSettings.compatibilityFlags, flag]
                      : formData.defaultWorkerSettings.compatibilityFlags.filter(f => f !== flag);
                    handleInputChange('defaultWorkerSettings', {
                      ...formData.defaultWorkerSettings,
                      compatibilityFlags: flags
                    });
                  }}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">{flag}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="jwtSecretTemplate" className="block text-sm font-medium mb-2">
            JWT Secret Template
          </label>
          <input
            id="jwtSecretTemplate"
            type="password"
            value={formData.jwtSecretTemplate}
            onChange={(e) => handleInputChange('jwtSecretTemplate', e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder="Enter JWT secret template (minimum 32 characters)"
            minLength={32}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Template for generating JWT secrets for marketplace app workers. Must be at least 32 characters.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {isSubmitting ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </form>
    </div>
  );
}